// src/context/AuthContext.tsx
import { apiFetch } from "@/api/APIClient";
import {
  GetMySummaryResponseDto,
  SignupUserWithEmailRequestDto,
} from "@/api/generated/generatedTypes";
import { useSignupWithEmail } from "@/api/user/useSignupWithEmail";
import { AuthError, Session, User as SupaUser } from "@supabase/supabase-js";
import { Crisp } from "crisp-sdk-web";
import { usePostHog } from "posthog-js/react";
import React, { createContext, useContext, useEffect, useState } from "react";
import supabase from "./Client";
import { exitViewAs, isViewingAs } from "./viewAs";

interface SignUpWithEmailParams {
  email: string;
  password: string;
  phone: string;
}

interface SignInWithEmailParams {
  email: string;
  password: string;
}

interface AuthContextType {
  user: SupaUser | null;
  session: Session | null;
  signOut: () => Promise<void>;
  isLoading: boolean;
  loginWithKakao: () => Promise<AuthError | null>;
  signInWithEmail: (params: SignInWithEmailParams) => Promise<void>;
  signupWithEmail: (params: SignupUserWithEmailRequestDto) => Promise<void>;
  supabase: typeof supabase;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  signOut: async () => {},
  isLoading: false,
  loginWithKakao: async () => {
    throw new Error("loginWithKakao is not implemented");
  },
  signInWithEmail: async () => {
    throw new Error("signInWithEmail is not implemented");
  },
  signupWithEmail: async () => {
    throw new Error("signupWithEmail is not implemented");
  },
  supabase,
});

const BASE_URL = import.meta.env.VITE_WEB_BASE_URL;
const API_URL = import.meta.env.VITE_API_URL;
export const useAuth = () => useContext(AuthContext);

const fetchUserSummary = async (): Promise<GetMySummaryResponseDto> => {
  const response = await apiFetch("api/users/me/summary");
  if (!response.ok) throw new Error("GET api/users/me/summary failed");
  return response.json();
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<SupaUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { mutateAsync: signupWithEmailMutation } = useSignupWithEmail();
  const posthog = usePostHog();

  const signupWithEmail = async (params: SignupUserWithEmailRequestDto) => {
    const sessionData = await signupWithEmailMutation(params);
    if (sessionData) {
      setIsLoading(true);
      await supabase.auth.setSession(sessionData);
      // Explicitly alias anonymous ID to user ID before signup event
      if (sessionData.user?.id) {
        const anonymousId = posthog.get_distinct_id(); // Get current anonymous ID
        console.log("🔍 PostHog alias:", { anonymousId, userId: sessionData.user.id });
        posthog.alias(sessionData.user.id, anonymousId); // Create explicit link

        // Fetch user profile and identify with person properties
        try {
          const userSummary = await fetchUserSummary();
          posthog.identify(sessionData.user.id, {
            email: userSummary.email,
            name: userSummary.name,
            nickname: userSummary.nickname,
          });
          console.log("🔍 PostHog identify called with user properties");
        } catch (error) {
          console.error("Failed to fetch user summary for PostHog:", error);
          posthog.identify(sessionData.user.id); // Fallback without properties
        }
        console.log("🔍 PostHog distinct_id:", posthog.get_distinct_id());
      }
      posthog.capture("user_signed_up", {
        method: "email",
      });
      console.log("🔍 user_signed_up captured");
    }
  };

  const loginWithKakao = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "kakao",
      options: { redirectTo: `${BASE_URL}/oauth-redirect` },
    });
    if (error) {
      alert(error.message);
      console.error(error);
    } else {
      posthog.capture("user_signup_initiated", {
        method: "kakao",
      });
    }
    return error;
  };

  const signInWithEmail = async ({ email, password }: SignInWithEmailParams) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) {
      throw new Error(`Error signing in with email: ${error?.message ?? "Unknown error"}`);
    }

    // After successful Supabase login, verify the user exists in our app DB
    try {
      const response = await apiFetch("/api/auth/verify-user", {
        method: "POST",
      });

      if (!response.ok) {
        if (response.status === 404) {
          console.error("User exists in Supabase but not in our database. Logging out.");
          // Immediately sign out to clear the invalid session
          await supabase.auth.signOut();
          throw new Error("로그인 에러: 유저가 존재하지 않습니다.");
        }
        // Handle other potential errors during verification
        throw new Error(`유저 인증 실패: ${response.status}`);
      }
    } catch (verificationError) {
      // Ensure user is signed out if verification fails for any reason
      await supabase.auth.signOut();
      throw verificationError; // Re-throw the error to be caught by the UI
    }

    // Alias and identify for login (same as signup)
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (session?.user?.id) {
      const anonymousId = posthog.get_distinct_id();
      posthog.alias(session.user.id, anonymousId);

      // Fetch user profile and identify with person properties
      try {
        const userSummary = await fetchUserSummary();
        posthog.identify(session.user.id, {
          email: userSummary.email,
          name: userSummary.name,
          nickname: userSummary.nickname,
        });
      } catch (error) {
        console.error("Failed to fetch user summary for PostHog:", error);
        posthog.identify(session.user.id); // Fallback without properties
      }
    }

    posthog.capture("user_logged_in", {
      method: "email",
    });
  };

  useEffect(() => {
    setIsLoading(true);
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session) {
        // Only identify if returning user (already identified), don't alias.
        // In view-as mode the summary belongs to another user and would overwrite the admin's profile.
        if (session.user?.id && !isViewingAs()) {
          try {
            const userSummary = await fetchUserSummary();
            posthog.identify(session.user.id, {
              email: userSummary.email,
              name: userSummary.name,
              nickname: userSummary.nickname,
            });
          } catch (error) {
            console.error("Failed to fetch user summary for PostHog:", error);
            posthog.identify(session.user.id); // Fallback without properties
          }
        }
      }
      // setIsLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      /*
       * event types:
       * 'INITIAL_SESSION'
       * 'PASSWORD_RECOVERY'
       * 'SIGNED_IN'
       * 'SIGNED_OUT'
       * 'TOKEN_REFRESHED'
       * 'USER_UPDATED'
       */
      if (event === "SIGNED_IN" && session) {
        setSession(session);
        setUser(session.user ?? null);
        // Don't identify here - it's already done in signupWithEmail/signInWithEmail
      }
      if (event === "SIGNED_OUT") {
        setSession(null);
        setUser(null);
        posthog.reset(); // Clear identified state on logout

        // Reset Crisp session on logout
        if (import.meta.env.VITE_CRISP_WEBSITE_ID) {
          try {
            Crisp.session.reset();
          } catch (error) {
            console.error("Failed to reset Crisp session:", error);
          }
        }
      }
      if (event === "TOKEN_REFRESHED" && session) {
        setSession(session);
        setUser(session.user ?? null);
      }
      if (event === "PASSWORD_RECOVERY" && session) {
        setSession(session);
        setUser(session.user ?? null);
        // Redirect to password update page
        window.location.href = "/update-password";
      }
      setIsLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    if (isViewingAs()) {
      exitViewAs();
      return;
    }

    const { error } = await supabase.auth.signOut();
    if (error) throw error;

    // Reset Crisp session on logout
    if (import.meta.env.VITE_CRISP_WEBSITE_ID) {
      try {
        Crisp.session.reset();
      } catch (error) {
        console.error("Failed to reset Crisp session:", error);
      }
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        signOut,
        isLoading,
        loginWithKakao,
        signInWithEmail,
        signupWithEmail,
        supabase,
      }}
    >
      {!isLoading && children}
    </AuthContext.Provider>
  );
};
